import { Controller } from "react-hook-form";
import SwitchToggle from "./switch.toggle";
import {
  inputStyle,
  labelStyle,
  fieldStyle,
  errorStyle,
  inputWrapper,
  inputIcon,
  labelStyle12,
} from "./style";
import AutocompleteCustomA from "../custom/autocompletecustom";
import { useTranslation } from "react-i18next";

const FormField = ({
  fontSize12,
  name,
  control,
  label,
  labelFooter,
  placeholder,
  type = "text",
  required = false,
  errors,
  validationRules,
  filterType,
  style,
  funcfilterInput = () => {},
  switchConfig = {},
  options = [],
  icon,
  disabled = false,
  labelswitch,
  maxLength,
  onChange: onChangeExternal,
}) => {
  const hasLabel = Boolean(label);
  const hasLabelFooter = Boolean(labelFooter);
  const { t } = useTranslation();

  /* ========= COUNTER ========= */

  const Counter = ({ value }) =>
    maxLength ? (
      <div
        style={{
          fontSize: "12px",
          color: "#6B7280",
        }}
      >
        {(value || "").length}/{maxLength}
      </div>
    ) : null;

  /* ========= FOOTER ========= */

  const FooterLabel = ({ value }) =>
    hasLabelFooter || maxLength ? (
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <label
          style={{
            ...(fontSize12 ? labelStyle12 : labelStyle),
            fontSize: "12px",
            color: "#6B7280",
          }}
        >
          {labelFooter || ""}
        </label>

        <Counter value={value} />
      </div>
    ) : null;

  /* ================= SWITCH ================= */

  if (type === "switch") {
    const {
      width = "100%",
      gap = 8,
      direction = "column",
      justify = "flex-start",
      align = "flex-start",
      showLabel = true,
    } = switchConfig;

    return (
      <div
        style={{
          width,
          display: "flex",
          flexDirection: direction,
          justifyContent: justify,
          alignItems: align,
          gap,
          ...style,
        }}
      >
        {showLabel && hasLabel && <label style={labelStyle}>{label}</label>}

        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <SwitchToggle
              label={labelswitch}
              value={field.value}
              onChange={field.onChange}
              disabled={disabled}
            />
          )}
        />

        <FooterLabel />
      </div>
    );
  }

  /* ================= VIEW SWITCH ================= */

  if (type === "vswitch") {
    const {
      width = "100%",
      gap = 8,
      direction = "column",
      justify = "flex-start",
      align = "flex-start",
      showLabel = true,
    } = switchConfig;

    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const value = Number(field.value);

          let isActive = false;
          let text = "";

          if (name === "status") {
            isActive = value === 1;
            text = isActive
              ? t("hrms_setting_location_active_status")
              : t("hrms_setting_location_inactive_status");
          }

          if (name === "showInAttendance") {
            isActive = value === 1;
            text = isActive
              ? t("hrms_setting_location_form_show_atten")
              : t("hrms_setting_location_form_hide_atten");
          }

          return (
            <div
              style={{
                width,
                display: "flex",
                flexDirection: direction,
                justifyContent: justify,
                alignItems: align,
                gap,
                ...style,
              }}
            >
              {showLabel && hasLabel && (
                <label
                  style={{
                    ...labelStyle,
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {label} :
                  <div
                    style={{
                      paddingLeft: 10,
                      paddingRight: 10,
                      backgroundColor: isActive
                        ? "var(--color-functional-foreground-success-subtle)"
                        : "var(--color-functional-foreground-error-subtle)",
                      color: isActive
                        ? "var(--color-functional-content-success)"
                        : "var(--color-functional-content-error)",
                      minWidth: "66px",
                      minHeight: "26px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: 500,
                    }}
                  >
                    {text}
                  </div>
                </label>
              )}

              <FooterLabel />
            </div>
          );
        }}
      />
    );
  }

  /* ================= SELECT ================= */

  if (type === "select") {
    return (
      <div style={fieldStyle}>
        {hasLabel && (
          <label style={labelStyle}>
            {label} {required && <span style={{ color: "#FF4D4F" }}>*</span>}
          </label>
        )}

        <Controller
          name={name}
          control={control}
          rules={validationRules}
          render={({ field: { onChange, value = null } }) => (
            <>
              <AutocompleteCustomA
                isClearable={false}
                disabled={disabled}
                options={options}
                value={value}
                onChange={(val) => {
                  onChange(val);
                  onChangeExternal?.(val);
                }}
              />

              <FooterLabel value={value} />
            </>
          )}
        />
      </div>
    );
  }

  /* ================= INPUTNUM ================= */

  if (type === "inputnum") {
    return (
      <div style={fieldStyle}>
        {hasLabel && (
          <label style={labelStyle}>
            {label} {required && <span style={{ color: "#FF4D4F" }}>*</span>}
          </label>
        )}

        <Controller
          name={name}
          control={control}
          rules={{
            validate: (value) => {
              if (value === "" || value === null || value === undefined) {
                return t("hrms_setting_location_form_error");
              }

              const str = String(value);

              if (/^0+$/.test(str)) {
                return t("hrms_setting_location_form_error");
              }

              if (Number(value) < 1) {
                return t("hrms_setting_location_form_error");
              }

              return true;
            },
            ...validationRules,
          }}
          render={({ field }) => (
            <>
              <div style={inputWrapper}>
                {icon && <img src={icon} alt="icon" style={inputIcon} />}

                <input
                  {...field}
                  type="number"
                  data-error={!!errors?.[name]}
                  step={1}
                  min={1}
                  disabled={disabled}
                  placeholder={placeholder}
                  style={{
                    ...inputStyle,
                    height: "40px",
                    paddingLeft: icon ? "38px" : "12px",
                    ...style,
                    borderRadius: 8,
                  }}
                  onKeyDown={(e) => {
                    if (
                      e.key === "0" &&
                      (field.value === "" || field.value === null)
                    ) {
                      e.preventDefault();
                    }

                    if (["e", "E", "+", "-", "."].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value === "" ? "" : value);
                  }}
                />
              </div>

              {errors?.[name] && (
                <span style={errorStyle}>*{errors[name].message}</span>
              )}

              <FooterLabel value={field.value} />
            </>
          )}
        />
      </div>
    );
  }

  /* ================= INPUTDECI ================= */

  if (type === "inputdeci") {
    return (
      <div style={fieldStyle}>
        {hasLabel && (
          <label style={labelStyle}>
            {label} {required && <span style={{ color: "#FF4D4F" }}>*</span>}
          </label>
        )}

        <Controller
          name={name}
          control={control}
          rules={{
            required: t("hrms_setting_location_form_error"),
            validate: (value) =>
              value.trim() !== "" || t("hrms_setting_location_form_error"),
          }}
          render={({ field }) => (
            <>
              <div style={inputWrapper}>
                {icon && <img src={icon} alt="icon" style={inputIcon} />}

                <input
                  {...field}
                  type="text"
                  disabled={disabled}
                  data-error={!!errors?.[name]}
                  placeholder={placeholder}
                  maxLength={maxLength}
                  style={{
                    ...inputStyle,
                    height: "40px",
                    paddingLeft: icon ? "38px" : "12px",
                    ...style,
                    borderRadius: 8,
                  }}
                  onChange={(e) => {
                    let value = e.target.value;

                    value = value.replace(/[^0-9.]/g, "");

                    const parts = value.split(".");
                    if (parts.length > 2) {
                      value = parts[0] + "." + parts.slice(1).join("");
                    }

                    if (maxLength) {
                      value = value.slice(0, maxLength);
                    }

                    field.onChange(value);
                  }}
                />
              </div>

              {errors?.[name] && (
                <span style={errorStyle}>*{errors[name].message}</span>
              )}

              <FooterLabel value={field.value} />
            </>
          )}
        />
      </div>
    );
  }

  /* ================= INPUT DEFAULT ================= */

  return (
    <div style={fieldStyle}>
      {hasLabel && (
        <label style={fontSize12 ? labelStyle12 : labelStyle}>
          {label} {required && <span style={{ color: "#FF4D4F" }}>*</span>}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        rules={
          validationRules ||
          (required
            ? {
                required: t("hrms_setting_location_form_error"),
                validate: (value) =>
                  value.trim() !== "" || t("hrms_setting_location_form_error"),
              }
            : {})
        }
        render={({ field }) => (
          <>
            <div style={inputWrapper}>
              {icon && <img src={icon} alt="icon" style={inputIcon} />}

              <input
                {...field}
                disabled={disabled}
                placeholder={placeholder}
                type={type}
                data-error={!!errors?.[name]}
                maxLength={maxLength}
                style={{
                  ...inputStyle,
                  height: "40px",
                  paddingLeft: icon ? "38px" : "12px",
                  ...style,
                  borderRadius: 8,
                }}
                onChange={(e) => {
                  let value = e.target.value;

                  if (maxLength) {
                    value = value.slice(0, maxLength);
                  }

                  field.onChange(value);
                }}
              />
            </div>

            {errors?.[name] && (
              <span style={errorStyle}>*{errors[name].message}</span>
            )}

            <FooterLabel value={field.value} />
          </>
        )}
      />
    </div>
  );
};

export default FormField;
